/*
 * Copyright (c) 2021. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import type { Theme } from '@mui/material';
import {
  type AggregationMetric,
  defaultBatchData,
  fromObjectEntries,
  type GenericPricesRecord,
  MerchantPriceOptions,
  Metrics,
  OperationType,
  objectKeys,
  PricesType,
  ShapeObjectives,
} from '@poweralpha/common';
import dayjs from 'dayjs';
import { range, uniqueId } from 'lodash';
import { highlightBackground } from '../styles';
import {
  AssetType,
  type CustomDeficitPenalty,
  type ESSBatchForm,
  type MarketInputs,
  type PlantPlotLines,
  ShapeInputType,
  ShapeLocation,
  type SimulateBatchForm,
  type SimulateForm,
  type ThermalBatchForm,
} from './models';

export const DEFAULT_BLOCK_DEFINITIONS = range(12).map(() => new Array(24).fill(''));

export const METRIC_LABEL_MAPPING: { [key in Metrics]: string } = {
  'poi.plant_out': 'Delivery at POI',
  'poi.power_in': 'Import at POI',
  'poi.power_out': 'Export at POI',
  'poi.clipped': 'Clipped at POI',
  'shape.profile': 'Shape',
  'shape.excess': 'Shape excess',
  'shape.deficit': 'Shape deficit',
  'shape.delivery': 'Shape delivery',
  'shape.50_pct_deficit': 'Shape deficit at 50% load',
  'shape.90_pct_deficit': 'Shape deficit at 90% load',
  'shape.normalized_delivery': 'Shape normalized delivery',
  'shape.normalized_deficit': 'Shape normalized deficit',
  'shape.deficit_cost': 'Market Deficit Cost',
  'shape.excess_revenue': 'Market Excess Revenue',
  'shape.buy_price': 'Shape Buy Price',
  'shape.sell_price': 'Shape Sell Price',
  'price_dispatch.sell_price': 'Sell price',
  'price_dispatch.buy_price': 'Buy price',
  'price_dispatch.sell_power': 'Energy sell',
  'price_dispatch.buy_power': 'Energy Buy',
  'price_dispatch.revenue': 'Net Market Revenue',
  'pv.power_out': 'PV generation',
  'wind.power_out': 'Wind generation',
  'bess-1.charge': 'BESS charge',
  'bess-1.discharge': 'BESS discharge',
};

const allModesMetrics = [
  Metrics.poi_plant_out,
  Metrics.poi_power_in,
  Metrics.poi_power_out,
  Metrics.poi_clipped,
];

export const OPERATION_MODE_METRIC_MAPPING = new Map<OperationType, Array<Metrics>>([
  [
    OperationType.PRICE_DISPATCH,
    [
      ...allModesMetrics,
      Metrics.price_dispatch_sell_price,
      Metrics.price_dispatch_buy_price,
      Metrics.price_dispatch_sell_power,
      Metrics.price_dispatch_buy_power,
      Metrics.price_dispatch_revenue,
    ],
  ],
  [
    OperationType.FIXED_SHAPE,
    [
      ...allModesMetrics,
      Metrics.shape_profile,
      Metrics.shape_excess,
      Metrics.shape_deficit,
      Metrics.shape_delivery,
      Metrics.shape_50_pct_deficit,
      Metrics.shape_90_pct_deficit,
      Metrics.shape_normalized_deficit,
      Metrics.shape_normalized_delivery,
    ],
  ],
  [
    OperationType.SHAPE_WITH_PRICE_DISPATCH,
    [
      ...allModesMetrics,
      Metrics.shape_profile,
      Metrics.shape_excess,
      Metrics.shape_deficit,
      Metrics.shape_deficit_cost,
      Metrics.shape_excess_revenue,
      Metrics.shape_buy_price,
      Metrics.shape_sell_price,
    ],
  ],
  [OperationType.AS_AVAIL_WO_CLIP_CAPTURE, allModesMetrics],
  [OperationType.AS_AVAIL_W_CLIP_CAPTURE, allModesMetrics],
]);

export const ASSET_TYPE_METRIC_MAPPING = new Map<AssetType, Array<Metrics>>([
  [AssetType.PV, [Metrics.pv_power_out]],
  [AssetType.ESS, [Metrics.bess_charge, Metrics.bess_discharge]],
  [AssetType.WIND, [Metrics.wind_power_out]],
]);

export const OPERATION_MODE_GRID_CHARGE_MAPPING = new Map<OperationType, boolean>([
  [OperationType.FIXED_SHAPE, false],
  [OperationType.PRICE_DISPATCH, true],
  [OperationType.SHAPE_WITH_PRICE_DISPATCH, false],
  [OperationType.AS_AVAIL_W_CLIP_CAPTURE, false],
  [OperationType.AS_AVAIL_WO_CLIP_CAPTURE, false],
]);

export const DEFAULT_NEW_BESS: SimulateForm['ess'][0] = {
  configuration: null,
  request: null,
  nameplate_power: '100',
  duration: '4',
  throughput_cost: '0',
  max_annual_cycles: '',
  init_soc: '50',
  ramp_cost: '0',
  idle_aux_mw: null,
  active_aux_mw_per_mw: null,
  charge_from_pv: null,
  charge_from_wind: null,
  self_discharge_rate: null,
  allow_custom_parameters: false,
  usable_capacities_at_poi: null,
  roundtrip_efficiency: null,
};

export const DEFAULT_NEW_THERMAL: SimulateForm['thermal'][0] = {
  thermal_generator: null,
  unit_count: 1,
  design_mw: '',
  turndown_ratio: '',
  heat_rate: '',
  annual_capacity_factor: '',
  annual_energy_max: '',
  is_fuel_price_fixed: true,
  fixed_fuel_price: '',
  fuel_price: null,
  fuel_price_start_year: null,
};

export const SIMULATE_FORM_DEFAULT_VALUES: SimulateForm = {
  project: {
    cod: new Date().toISOString(),
    term: '1',
    poi_mw_ac: '100',
    operation_type: OperationType.PRICE_DISPATCH,
    grid_charge: true,
    includePostProcessingInputs: false,
    aux_usage: 'no_aux',
    granularity: '1h',
    sim_horizon: 'Monthly',
    sim_horizon_hours: '1',
    aggregation_metrics: [],
    block_definitions: [],
    includeMarketSpecificInputs: false,
    reg_lower_enablement: '20',
    reg_raise_enablement: '20',
    regulation_max: '100',
    poi_in_max_mw: null,
    poi_out_max_mw: null,
    variable_poi: {
      poi_in_profile: null,
      poi_in_data_start_year: null,
      poi_in_scale: '',
      poi_out_profile: null,
      poi_out_data_start_year: null,
      poi_out_scale: '',
    },
    pvs_shared_injection_limit: false,
  },
  shape: {
    profile: null,
    data_start_year: null,
    objective: ShapeObjectives.minimize_deficit,
    deficit_penalty_type: ShapeInputType.SINGLE,
    deficit_penalty: '100',
    deficit_penalty_blocks: range(12).map(() => new Array(24).fill('100')),
    excess_revenue_type: ShapeInputType.SINGLE,
    excess_revenue: '0.01',
    excess_revenue_blocks: range(12).map(() => new Array(24).fill('0.01')),
    scale: '',
    shape_location: ShapeLocation.FTM,
  },
  ess: [],
  pv: {
    pvsim_configuration: null,
    pvsim_request: null,
    n_inverters: '1',
    ilr: '1.1',
    weather_data_start_date: null,
    weather_data_end_date: null,
    data_start_year: null,
    module_degradation: false,
    optimize_ptc: false,
    optimize_rec: false,
  },
  wind: {
    profile: null,
    n_turbines: null,
    aux_power_losses: '0',
    transmission_loss: '0',
    data_start_year: null,
    optimize_ptc: false,
    optimize_rec: false,
  },
  h2: {
    electrolyzer: null,
    liquefier: null,
    electrolyzer_design_tpd: null,
    electrolyzer_storage_tonnes: null,
    liquefier_design_tpd: null,
    max_tpd: null,
    min_tpd: null,
    revenue: null,
  },
  thermal: [],
  simple_markets: true,
  buy_prices: null,
  sell_prices: null,
  reg_raise_market: null,
  reg_lower_market: null,
  ptc: {
    base_rate: '30',
    escalation_rate: '2.5',
    base_year: '2025',
    tenor: '10',
  },
  rec: {
    view: 'curve',
    prices: null,
    fixed_price_base_rate: '',
    fixed_price_esc: '',
  },
  assets: [],
  essIdx: [],
  thermalIdx: [],
};

export const BATCH_PV_DEFAULT_VALUES: Partial<SimulateBatchForm['batch_params']> = {
  pv_n_inverters: defaultBatchData,
  pv_ilr: defaultBatchData,
};

export const BATCH_ESS_DEFAULT_VALUES: ESSBatchForm = {
  ess_power: defaultBatchData,
  ess_duration: defaultBatchData,
};

export const BATCH_THERMAL_DEFAULT_VALUES: ThermalBatchForm = {
  unit_count: defaultBatchData,
  design_mw: defaultBatchData,
  annual_capacity_factor: defaultBatchData,
  annual_energy_max: defaultBatchData,
};

export const BATCH_WIND_DEFAULT_VALUES: Partial<SimulateBatchForm['batch_params']> = {
  wind_n_turbines: defaultBatchData,
};

export const BATCH_SHAPE_DEFAULT_VALUES: Partial<SimulateBatchForm['batch_params']> = {
  shape_scale: defaultBatchData,
};

export const BATCH_H2_DEFAULT_VALUES: Partial<SimulateBatchForm['batch_params']> = {
  h2_electrolyzer_tpd: defaultBatchData,
  h2_electrolyzer_storage: defaultBatchData,
  h2_liquefier_tpd: defaultBatchData,
  h2_min_tpd: defaultBatchData,
  h2_max_tpd: defaultBatchData,
  h2_revenue: defaultBatchData,
};

export const BATCH_SIMULATE_FORM_DEFAULT_VALUES: SimulateBatchForm = {
  config: null,
  request: null,
  view: 'form',
  batch_params: {
    ...BATCH_PV_DEFAULT_VALUES,
    ...BATCH_WIND_DEFAULT_VALUES,
    ...BATCH_SHAPE_DEFAULT_VALUES,
    ...BATCH_H2_DEFAULT_VALUES,
    ess: [BATCH_ESS_DEFAULT_VALUES],
    thermal: [BATCH_THERMAL_DEFAULT_VALUES],
    file: null,
    poi_mw: defaultBatchData,
  } as SimulateBatchForm['batch_params'],
};

export const simHorizonOptions = ['Hours', 'Weekly', 'Daily', 'Monthly', 'Annual'];

export const priceTypesForApi: Array<PricesType> = [
  PricesType.MARKET,
  PricesType.ONOFFPEAK,
  PricesType.MULTI_YEAR,
  PricesType.REC,
];

export const defaultPrices: MarketInputs = {
  merchant_price_option: MerchantPriceOptions.MARKET_PRICES,
  market_prices: null,
  multiyear_prices: null,
  onoffpeak_prices: null,
  data_start_year: null,
};

export const defaultRegPrices: MarketInputs = {
  merchant_price_option: MerchantPriceOptions.MULTI_YEAR_PRICES,
  market_prices: null,
  multiyear_prices: null,
  onoffpeak_prices: null,
  data_start_year: null,
};

export const getSummaryDashboardLineMetaMap = (
  theme: Theme,
  numBess: number,
  numThermal: number,
): Record<keyof PlantPlotLines, { color: string; customLabel?: string }> => ({
  bess_charge: {
    color: highlightBackground(theme, { color: '#3b6bd9' }),
    customLabel: 'ESS Charge',
  },
  bess_discharge: {
    color: highlightBackground(theme, { color: '#0040e1' }),
    customLabel: 'ESS Discharge',
  },
  pv: { color: highlightBackground(theme, { color: '#facf00' }), customLabel: 'PV' },
  shape: { color: highlightBackground(theme, { color: '#FFE7C3' }) },
  shape_deficit: { color: highlightBackground(theme, { color: '#ff0d0d' }) },
  shape_excess: { color: highlightBackground(theme, { color: '#41ff0d' }) },
  wind: { color: highlightBackground(theme, { color: '#0dc7ff' }) },
  markets_sell: { color: highlightBackground(theme, { color: '#ff860d' }) },
  sell_price: { color: highlightBackground(theme, { color: '#c4680c' }) },
  markets_buy: { color: highlightBackground(theme, { color: '#3abb4d' }) },
  buy_price: { color: highlightBackground(theme, { color: '#2d8f3c' }) },
  plant_out: { color: highlightBackground(theme, { color: '#888888' }) },
  h2_power_in: { color: highlightBackground(theme, { color: '#3d4be3' }) },
  ...fromObjectEntries(
    range(1, numBess).map((i) => [
      `bess${i + 1}_charge`,
      {
        color: highlightBackground(theme, {
          color: '#3b6bd9',
          coefficient: numBess ? (1 / numBess) * i : 0,
        }),
        customLabel: `ESS ${i + 1} Charge`,
      },
    ]),
  ),
  ...fromObjectEntries(
    range(1, numBess).map((i) => [
      `bess${i + 1}_discharge`,
      {
        color: highlightBackground(theme, {
          color: '#0040e1',
          coefficient: numBess ? (1 / numBess) * i : 0,
        }),
        customLabel: `ESS ${i + 1} Discharge`,
      },
    ]),
  ),
  ...fromObjectEntries(
    range(numThermal).map((i) => [
      `thermal${i + 1}_power_out`,
      {
        color: highlightBackground(theme, {
          color: '#737373',
          coefficient: numThermal ? (1 / numThermal) * i : 0,
        }),
        customLabel: `Thermal ${i + 1} Power Out`,
      },
    ]),
  ),
});

export const granularityLabelMap: Record<string, string> = {
  '1h': 'Hourly',
  '30min': '30min',
  '15min': '15min',
  '10min': '10min',
  '5min': '5min',
};

export const auxUsageOptions = ['no_aux', 'count', 'meet'];

export const marketPricesCustomAttrMap: Partial<Record<keyof GenericPricesRecord, string>> = {
  year: 'Year',
  publish_date: 'Publish Date',
  created_at: 'Created at',
  source: 'Source',
};
export const pricesCustomAttrMap: Partial<Record<keyof GenericPricesRecord, string>> = {
  start_date: 'Start Date',
  end_date: 'End Date',
  price_data_type: 'Price Data Type',
  granularity: 'Granularity',
  publish_date: 'Publish Date',
  created_at: 'Created at',
  source: 'Source',
};
export const pricesCustomAttrs = [
  ...Object.keys(pricesCustomAttrMap),
  ...Object.keys(marketPricesCustomAttrMap),
] as Array<keyof GenericPricesRecord>;

export const getUniqValueCountsObjForBlocks = (
  blockDefinitions: Array<Array<string>> | undefined,
): { [key: string]: number } => {
  if (!blockDefinitions) {
    return {};
  }
  const uniqValuesCount: { [key: string]: number } = {};
  blockDefinitions.forEach((bd) => {
    bd.forEach((d) => {
      const key = d === '' ? 'empty' : d;
      if (uniqValuesCount[key]) {
        uniqValuesCount[key] += 1;
      } else {
        uniqValuesCount[key] = 1;
      }
    });
  });

  return uniqValuesCount;
};

export const getUniqValueCountsForBlocks = (
  blockDefinitions: Array<Array<string>> | undefined,
  units = '',
): string => {
  const uniqValuesCount = getUniqValueCountsObjForBlocks(blockDefinitions);

  const unitsFormatted = units ? ` ${units}` : '';
  return Object.keys(uniqValuesCount)
    .map((key) => `${key}${unitsFormatted} - ${uniqValuesCount[key]}`)
    .join(', ');
};

export interface CustomDeficitMetrics {
  label: string;
  shapeInputCustomBlocks: Array<CustomDeficitPenalty>;
}

export const getCustomDeficitMetrics = (
  deficitPenaltyBlocks: Array<Array<string>> | null | undefined,
  postProcessingAggregationMetrics: AggregationMetric[] | undefined,
) => {
  const deficitMetricsOptions: Record<string, CustomDeficitMetrics> = {};
  const uniqBlocks = objectKeys(
    getUniqValueCountsObjForBlocks(deficitPenaltyBlocks ?? []),
  ) as string[];
  (postProcessingAggregationMetrics ?? [])
    .filter(
      (am) =>
        am.metric &&
        am.aggregation_methods &&
        [
          Metrics.shape_deficit,
          Metrics.shape_50_pct_deficit,
          Metrics.shape_90_pct_deficit,
        ].includes(am.metric) &&
        am.aggregation_methods.includes('sum'),
    )
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: refactor
    .forEach((am) => {
      if (am.metric) {
        let aggMetric = `sum_${am.metric.replace(/\./g, '_')}`;
        if (am.reliability) {
          aggMetric = `${aggMetric}_${am.reliability * 100}_pct_net`;
        }
        let aggMetricLabel = `Sum ${METRIC_LABEL_MAPPING[am.metric]}`;
        if (am.reliability) {
          aggMetricLabel = `${aggMetricLabel} ${am.reliability * 100} Percentage`;
        }

        let combinations: CustomDeficitPenalty[] = [];
        const blocks = am.groupby_blocks ? uniqBlocks : [];
        const months = am.groupby_month
          ? range(0, 12).map((i) => dayjs().month(i).format('MMMM'))
          : [];

        if (am.groupby_month && am.groupby_blocks) {
          // combinations of months and blocks
          combinations = months.reduce(
            (acc, month) => {
              blocks.forEach((block) => {
                acc.push({
                  id: uniqueId('shapeInputCustomBlocks'),
                  custom_block: `${month}_${block}`,
                  custom_block_label: `${month} ${block}`,
                  deficit_penalty: 0,
                });
              });
              return acc;
            },
            [] as Array<CustomDeficitPenalty>,
          );
        } else if (am.groupby_month && !am.groupby_blocks) {
          // only months
          combinations = months.map((month) => ({
            id: uniqueId('shapeInputCustomBlocks'),
            custom_block: month,
            custom_block_label: month,
            deficit_penalty: 0,
          }));
        } else if (!am.groupby_month && am.groupby_blocks) {
          // only blocks
          combinations = blocks.map((block) => ({
            id: uniqueId('shapeInputCustomBlocks'),
            custom_block: block,
            custom_block_label: block,
            deficit_penalty: 0,
          }));
        }
        deficitMetricsOptions[aggMetric] = {
          label: aggMetricLabel,
          shapeInputCustomBlocks: combinations,
        };
      }
    });
  return deficitMetricsOptions;
};
